import type React from 'react';
import {
  AppSelect,
  AppSelectContent,
  AppSelectItem,
  AppSelectTrigger,
} from '@/components/shared/AppSelect';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useIsMobile } from '@/hooks/useIsMobile';
import { cn } from '@/lib/utils/cn';
import { useNotificationsSheet } from './hooks/useNotificationsSheet';
import { NotificationItem } from './NotificationItem';
import type { NotificationsPopoverProps } from './types';

export function NotificationsPopover({
  open,
  onOpenChange,
  trigger,
}: NotificationsPopoverProps) {
  const {
    t,
    tab,
    subTab,
    resourceType,
    companySearchInput,
    setSearch,
    allItems,
    grouped,
    unreadCount,
    isLoading,
    isFetchingNextPage,
    isMarkAllPending,
    handleCompanySearchChange,
    handleMarkAllRead,
    handleDismiss,
    handleReactivate,
    bottomRef,
  } = useNotificationsSheet(open);

  const RESOURCE_TYPE_OPTIONS = [
    { value: 'CERTIFICATE', label: t('resourceTypes.CERTIFICATE') },
    { value: 'AUDITS', label: t('resourceTypes.AUDITS') },
    { value: 'DOCUMENTS', label: t('resourceTypes.DOCUMENTS') },
    { value: 'ASSESSMENTS', label: t('resourceTypes.ASSESSMENTS') },
    { value: 'QUESTIONNAIRES', label: t('resourceTypes.QUESTIONNAIRES') },
    { value: 'CAPA', label: t('resourceTypes.CAPA') },
    { value: 'CASCADE-MAPPING', label: t('resourceTypes.CASCADE_MAPPING') },
    { value: 'ENTITY-SCREENING', label: t('resourceTypes.ENTITY_SCREENING') },
    { value: 'TRACING', label: t('resourceTypes.TRACING') },
    { value: 'REPORTS', label: t('resourceTypes.REPORTS') },
    { value: 'SLCP', label: t('resourceTypes.SLCP') },
    { value: 'COMPANIES', label: t('resourceTypes.COMPANIES') },
  ];

  const GROUP_LABELS: Record<string, string> = {
    today: t('groups.today'),
    yesterday: t('groups.yesterday'),
    thisWeek: t('groups.thisWeek'),
    earlier: t('groups.earlier'),
  };

  const tabItems: { id: 'activity' | 'notifications'; label: string }[] = [
    { id: 'activity', label: t('tabs.activity') },
    { id: 'notifications', label: t('tabs.notifications') },
  ];

  const isActivity = tab === 'activity';
  const emptyKey = isActivity
    ? subTab === 'dismissed'
      ? 'dismissed'
      : 'activity'
    : 'notifications';
  const emptyHintKey = isActivity
    ? subTab === 'dismissed'
      ? 'dismissedHint'
      : 'activityHint'
    : 'notificationsHint';

  const isMobile = useIsMobile();

  const content = (
    <>
      {/* Header */}
      <div className='pt-5 pb-0 shrink-0'>
        <div className='flex items-center justify-between mb-0.5 px-6'>
          <span className='text-sm font-semibold'>{t('title')}</span>
          {tab === 'notifications' && unreadCount > 0 && (
            <Button
              variant='ghost'
              size='default'
              onClick={handleMarkAllRead}
              disabled={isMarkAllPending}
              className='text-muted-foreground hover:text-foreground h-auto py-1 px-2'
            >
              {t('markAllRead')}
            </Button>
          )}
        </div>

        {/* Underline tabs — flush to left edge */}
        <div className='flex items-end px-6'>
          {tabItems.map(item => (
            <button
              key={item.id}
              type='button'
              onClick={() =>
                setSearch({
                  nTab: item.id,
                  nSubTab: 'active',
                  nType: undefined,
                  nCompany: undefined,
                })
              }
              className={cn(
                'pr-4 pt-2 pb-2.5 text-xs font-medium border-b-2 -mb-px transition-colors whitespace-nowrap first:pl-0',
                tab === item.id
                  ? 'text-primary border-primary'
                  : 'text-muted-foreground border-transparent hover:text-foreground',
              )}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className='px-6 pt-2 pb-3 shrink-0 space-y-2'>
        {isActivity && (
          <ToggleGroup
            value={[subTab] as readonly string[]}
            onValueChange={v => {
              const next = v[v.length - 1] as
                | 'active'
                | 'dismissed'
                | undefined;
              if (next) setSearch({ nSubTab: next });
            }}
          >
            <ToggleGroupItem value='active'>
              {t('filters.yourActiveTasks')}
            </ToggleGroupItem>
            <ToggleGroupItem value='dismissed'>
              {t('filters.dismissed')}
            </ToggleGroupItem>
          </ToggleGroup>
        )}
        <div className='flex items-center gap-2'>
          <div className='w-[140px] shrink-0'>
            <AppSelect
              value={resourceType || '__all__'}
              onValueChange={v =>
                setSearch({ nType: v === '__all__' || !v ? undefined : v })
              }
            >
              <AppSelectTrigger
                placeholder={t('filters.allTypes')}
                displayValue={
                  resourceType
                    ? (RESOURCE_TYPE_OPTIONS.find(o => o.value === resourceType)
                        ?.label ?? resourceType)
                    : t('filters.allTypes')
                }
              />
              <AppSelectContent>
                <AppSelectItem value='__all__'>
                  {t('filters.allTypes')}
                </AppSelectItem>
                {RESOURCE_TYPE_OPTIONS.map(opt => (
                  <AppSelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </AppSelectItem>
                ))}
              </AppSelectContent>
            </AppSelect>
          </div>
          <div className='relative flex-1 min-w-0'>
            <i className='fa-light fa-magnifying-glass absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-xs pointer-events-none' />
            <Input
              value={companySearchInput}
              onChange={e => handleCompanySearchChange(e.target.value)}
              placeholder={t('filters.searchCompany')}
              className='h-7 text-xs pl-7 w-full'
            />
          </div>
        </div>
      </div>

      {/* Feed */}
      <div className='flex-1 overflow-y-auto min-h-0'>
        {isLoading ? (
          <div className='flex flex-col'>
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className='flex items-start gap-3 px-6 py-3'>
                <div className='size-8 rounded-lg bg-muted animate-pulse shrink-0' />
                <div className='flex-1 space-y-2 pt-0.5'>
                  <div className='h-3 bg-muted animate-pulse rounded w-3/4' />
                  <div className='h-2.5 bg-muted animate-pulse rounded w-1/2' />
                </div>
              </div>
            ))}
          </div>
        ) : allItems.length === 0 ? (
          <div className='flex flex-col items-center justify-center py-12 gap-3 text-center px-8'>
            <div className='size-10 rounded-full bg-muted flex items-center justify-center'>
              <i
                className={`fa-light ${isActivity ? 'fa-list-check' : 'fa-bell'} text-lg text-muted-foreground`}
              />
            </div>
            <div className='space-y-1'>
              <p className='text-sm font-medium'>{t(`empty.${emptyKey}`)}</p>
              <p className='text-xs text-muted-foreground'>
                {t(`empty.${emptyHintKey}`)}
              </p>
            </div>
          </div>
        ) : (
          <>
            {grouped.map(({ group, items }) => (
              <div key={group}>
                <div className='px-6 py-1.5 bg-popover sticky top-0 z-10'>
                  <span className='text-xs font-semibold text-muted-foreground uppercase tracking-wider'>
                    {GROUP_LABELS[group]}
                  </span>
                </div>
                {items.map(n => (
                  <NotificationItem
                    key={n.id}
                    notification={n}
                    tab={tab}
                    onDismiss={handleDismiss}
                    onReactivate={handleReactivate}
                  />
                ))}
              </div>
            ))}
            <div ref={bottomRef} className='py-2 flex justify-center'>
              {isFetchingNextPage && (
                <div className='flex items-center gap-2 text-xs text-muted-foreground'>
                  <div className='size-3 border-2 border-current border-t-transparent rounded-full animate-spin' />
                  {t('loading')}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side='right'
          showCloseButton
          className='!w-screen !max-w-screen p-0 flex flex-col overflow-hidden'
        >
          {content}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger render={trigger as React.ReactElement} />
      <PopoverContent
        align='end'
        sideOffset={8}
        className='w-115 p-0 flex flex-col h-150 overflow-hidden'
      >
        {content}
      </PopoverContent>
    </Popover>
  );
}
